import React from "react";
import { useOutletContext } from "react-router-dom";
import { FileText } from "lucide-react";
import { formatDateTime, formatDate } from "../lib/constants";

export default function ClientOverview() {
  const { project, activity, documents } = useOutletContext();
  const recentDocs = documents.slice(0, 5);
  const recentActivity = activity.slice(0, 6);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-testid="client-overview">
      <div className="lg:col-span-2 space-y-8">
        <section className="card-surface p-8">
          <div className="label mb-3">Summary</div>
          <p className="text-sm text-[color:var(--atech-text)] leading-relaxed whitespace-pre-wrap">
            {project.summary || <span className="text-[color:var(--atech-text-muted)]">No summary provided yet.</span>}
          </p>
        </section>

        <section className="card-surface p-8">
          <div className="label mb-5">Recent documents</div>
          {recentDocs.length === 0 ? (
            <div>
              <div className="text-sm text-[color:var(--atech-text)]">
                No documents shared yet.
              </div>
              <p className="text-xs text-[color:var(--atech-text-dim)] mt-1.5 leading-relaxed">
                Recent documents will appear here once A-Tech uploads them. You'll also get a notification when something new is shared.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-white/5">
              {recentDocs.map((d) => (
                <li key={d.id} className="py-3 flex items-center gap-3">
                  <FileText size={14} className="text-[color:var(--atech-text-muted)] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{d.title}</div>
                    <div className="text-xs text-[color:var(--atech-text-muted)]">
                      {formatDate(d.created_at)}
                      {d.drawing_number && <> · {d.drawing_number}{d.revision ? ` Rev ${d.revision}` : ""}</>}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <aside className="space-y-6">
        <section className="card-surface p-6">
          <div className="label mb-4">Details</div>
          <dl className="space-y-3 text-sm">
            <Row k="Reference" v={project.project_reference} />
            <Row k="Type" v={project.project_type} />
            {project.address && <Row k="Address" v={project.address} />}
            {project.start_date && <Row k="Start date" v={formatDate(project.start_date)} />}
            {project.due_date && <Row k="Due date" v={formatDate(project.due_date)} />}
            <Row k="Documents" v={project.document_count || 0} />
          </dl>
        </section>

        <section className="card-surface p-6">
          <div className="label mb-4">Recent activity</div>
          {recentActivity.length === 0 ? (
            <div>
              <div className="text-sm text-[color:var(--atech-text)]">
                Nothing yet.
              </div>
              <p className="text-xs text-[color:var(--atech-text-dim)] mt-1.5 leading-relaxed">
                Project activity will show up here — uploads, status changes, and updates from A-Tech.
              </p>
            </div>
          ) : (
            <ul className="space-y-3">
              {recentActivity.map((e) => (
                <li key={e.id} className="text-sm">
                  <div className="text-[color:var(--atech-text)]">{e.description}</div>
                  <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--atech-text-muted)] mt-0.5">
                    {formatDateTime(e.created_at)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </aside>
    </div>
  );
}

const Row = ({ k, v }) => (
  <div className="flex items-start justify-between gap-4">
    <dt className="label text-[color:var(--atech-text-muted)]">{k}</dt>
    <dd className="text-sm text-[color:var(--atech-text)] text-right">{v}</dd>
  </div>
);
